import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, type Pagina } from '../comun';
import type { components, operations } from '../tipos/pases-acceso';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

/** La página del pase (6.6) y «Mis pases» (6.7), con los tipos de `pases-acceso.yaml`. */
export type PasePublico = Esquema<'PublicPass'>;
export type MiPase = Esquema<'MyPass'>;
export type ListaMisPases = Esquema<'MyPassList'>;
export type EstadoPase = Esquema<'PassStatus'>;
export type ReenvioAceptado = Esquema<'ResendAccepted'>;
export type CuandoMisPases = NonNullable<NonNullable<operations['listMyPasses']['parameters']['query']>['when']>;

const publico = (negocioId: string, token: string): `/v1/${string}` => `/v1/public/businesses/${encodeURIComponent(negocioId)}/passes/${encodeURIComponent(token)}`;
const misPases = (perfilId: string): `/v1/${string}` => `/v1/customer-account/profiles/${encodeURIComponent(perfilId)}/passes`;

/**
 * La dirección del QR (`{pases.url_pase}/v1.<negocio>.<código>.<kid>.<firma>`, la da la API en `qr_url`) partida en el
 * negocio y el `PassToken` (`v1.<código>.<kid>.<firma>`) de las operaciones públicas. Null si no tiene esa forma.
 */
export function partesDelQr(direccion: string): { negocio: string; token: string } | null {
    const ultima = direccion.slice(direccion.lastIndexOf('/') + 1);
    const m = /^v1\.(biz_[0-9A-Za-z]+)\.([0-9A-Za-z]+)\.([0-9A-Za-z]+)\.([A-Za-z0-9_-]+)$/.exec(ultima);
    return m === null ? null : { negocio: m[1] ?? '', token: `v1.${m[2] ?? ''}.${m[3] ?? ''}.${m[4] ?? ''}` };
}

/**
 * Operaciones de Pases y Acceso en las páginas del cliente: las públicas (la página del pase y sus carteras) sin token ni
 * clave; las de «Mis pases» con el token de cliente.
 */
export function operacionesPasesCliente(api: ClienteApi) {
    return {
        /** getPublicPass (anónima): la página del pase, por el negocio y el `PassToken` del QR; `404` si no existe. */
        getPublicPass: (negocioId: string, token: string) => api.pedir<PasePublico>('GET', publico(negocioId, token), { sinSesion: true, cabeceras: { 'Accept-Language': 'es' } }),
        /** resendPublicPass (anónima): al contacto que ya tiene, 3 por pase y hora (`429`); `409` si ya no se puede. */
        resendPublicPass: (negocioId: string, token: string, clave = claveIdempotencia()) =>
            api.pedir<ReenvioAceptado>('POST', `${publico(negocioId, token)}/resend`, { sinSesion: true, idempotencia: clave }),
        /**
         * downloadAppleWalletPass (anónima, G422): el `.pkpass` como archivo. Sin `401`: `400` si el enlace está mal y `404`
         * si el pase no existe; la página los dice en palabras.
         */
        downloadAppleWalletPass: (negocioId: string, token: string) =>
            api.pedir<Blob>('GET', `${publico(negocioId, token)}/wallet/apple`, { sinSesion: true, comoBlob: 'application/vnd.apple.pkpass', cabeceras: { 'Accept-Language': 'es' } }),
        /** saveGoogleWalletPass (anónima): con `Accept: application/json`, el enlace de Google para guardarlo. */
        saveGoogleWalletPass: (negocioId: string, token: string) => api.pedir<{ save_url: string }>('GET', `${publico(negocioId, token)}/wallet/google`, { sinSesion: true }),

        /** listMyPasses (token de cliente): próximos o pasados en un negocio, con cursor. */
        listMyPasses: (perfilId: string, when: CuandoMisPases, pagina: Pagina = {}) => api.pedir<ListaMisPases>('GET', `${misPases(perfilId)}${consulta({ when, ...pagina })}`),
        /** getMyPass: uno, al abrirlo (con su QR al día). */
        getMyPass: (perfilId: string, paseId: string) => api.pedir<MiPase>('GET', `${misPases(perfilId)}/${encodeURIComponent(paseId)}`),
        /** resendMyPass: por su canal preferido con respaldo por email; 5 por pase y hora (`429`), `409` si ya no vale. */
        resendMyPass: (perfilId: string, paseId: string, clave = claveIdempotencia()) =>
            api.pedir<ReenvioAceptado>('POST', `${misPases(perfilId)}/${encodeURIComponent(paseId)}/resend`, { idempotencia: clave }),
    };
}

export type OperacionesPasesCliente = ReturnType<typeof operacionesPasesCliente>;
